import {
  LitElement,
  html,
  css,
  ifDefined,
  classMap,
  broadcastReceiver,
  t,
  utils,
  ref,
  createRef,
} from '@hh/global.js';
const { debounce } = utils

class HHSearchModal extends LitElement {
  static properties = {
    open: {
      type: Boolean,
      reflect: true,
    },
    openerSelector: {
      type: String,
      attribute: 'opener-selector',
    },
    searchTerm: {
      type: String,
    },
    searchActive: {
      type: Boolean,
      attribute: 'active',
      reflect: true,
    },
    noResults: {
      type: Boolean,
      attribute: 'no-results',
      reflect: true,
    },
    cachedResults: {
      type: Object,
    },
    transitionEnded: {
      type: Boolean,
    },
    hasOverflow: {
      type: Boolean,
    },
    searchResultsUrl: {
      type: String,
    }
  }

  constructor() {
    super()

    this.debounceSearchInput = debounce(this.onSearchInput.bind(this), 500)

    this.cachedResults = {}

    this.classes = {
      hidden: 'hidden',
    }
  }

  $focusTrap = createRef()
  $searchResultsContainer = createRef()

  get header() {
    return document.querySelector('.js-header') ?? null
  }

  get announcementBar() {
    return document.querySelector('.js-announcement-bar-section') ?? null
  }

  get opener() {
    return document.querySelector(this.openerSelector) ?? null
  }

  get form() {
    return (
      this.shadowRoot
        .querySelector('slot[name="search-form"]')
        ?.assignedNodes()[0] ?? null
    )
  }

  get searchInput() {
    return this.form?.querySelector('input[type="search"]') ?? null
  }

  get searchResults() {
    return (
      this.shadowRoot
        .querySelector('slot[name="search-results"]')
        ?.assignedNodes()[0] ?? null
    )
  }

  get searchClear() {
    return this.form?.querySelector('.js-search-clear') ?? null
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('search-modal', (...args) => {
      this.toggleFromDocument(...args)
    })

    window.addEventListener('search:close', this.toggleSearch.bind(this))
    window.addEventListener('keydown', this.onEscKey.bind(this))
    window.addEventListener('mobile-menu:opened', this.closeSearch.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver.disconnect()
    window.removeEventListener('search:close', this.toggleSearch)
    window.removeEventListener('keydown', this.onEscKey)
    window.removeEventListener('mobile-menu:opened', this.closeSearch)
    this.form.removeEventListener('submit', this.onFormSubmit)
    this.searchInput.removeEventListener('input', this.debounceSearchInput)
    this.searchInput.removeEventListener('keydown', this.onSearchKeyDown)
  }

  firstUpdated() {
    this.form.addEventListener('submit', this.onFormSubmit.bind(this))
    this.searchInput.addEventListener('input', this.onSearchInput.bind(this))
    this.searchInput.addEventListener(
      'keydown',
      this.onSearchKeyDown.bind(this)
    )
    this.searchClear.addEventListener('click', this.onSearchClear.bind(this))
    this.searchInput.value = this.searchTerm ?? ''
  }

  updated(changedProperties) {
    if (changedProperties.has('open')) {
      if (this.open) {
        document.documentElement.classList.add('overflow-hidden', 'search-open')
        this.emitSearchOpenEvent()
      } else {
        document.documentElement.classList.remove(
          'overflow-hidden',
          'search-open'
        )
      }
    }

    if (
      changedProperties.has('searchTerm') &&
      window.themeSettings.enablePredictiveSearch
    ) {
      this.searchChange(this.searchTerm)
    }

    if (changedProperties.has('searchActive')) {
      this.searchInput.setAttribute('aria-expanded', this.searchActive)
    }
  }

  async toggleFromDocument() {
    await this.updateHeaderHeight()
    await this.updateSearchFormHeight()
    this.toggleSearch()
  }

  closeFromModal(e) {
    const path = e.composedPath()
    if (
      path.some((element) => {
        return element.matches && element.matches('[data-close]')
      })
    ) {
      this.toggleSearch()
    }
  }

  toggleSearch() {
    this.open = !this.open
    this.opener.setAttribute('aria-expanded', this.open ? 'true' : 'false')

    if (!this.open) {
      this.opener.renderRoot.querySelector('button')?.focus({
        preventScroll: true,
        focusVisible: false,
      })
    }
  }

  closeSearch() {
    if (!this.open) return
    this.open = false
    this.opener.setAttribute('aria-expanded', 'false')
  }

  onCloseClick() {
    this.toggleSearch()
  }

  onEscKey(e) {
    if (e.key === 'Escape' && this.open) {
      this.toggleSearch()
    }
  }

  updateHeaderHeight() {
    return new Promise((resolve) => {
      this.headerHeight = this.header.offsetHeight
      this.announcementBarHeight = this.announcementBar?.offsetHeight || 0
      this.totalHeaderHeight = this.headerHeight + this.announcementBarHeight

      HH.setCSSVariable(
        '--hh-total-header-height',
        `${this.totalHeaderHeight}px`
      )
      resolve()
    })
  }

  updateSearchFormHeight() {
    new Promise((resolve) => {
      this.searchFormHeight = this.form.offsetHeight

      HH.setCSSVariable('--hh-search-form-height', `${this.searchFormHeight}px`)
      resolve()
    })
  }

  emitSearchOpenEvent() {
    this.dispatchEvent(
      new CustomEvent('search:opened', {
        bubbles: true,
        composed: true,
      })
    )
  }

  onFormSubmit(e) {
    this.checkIfSearchInputHasValue(e)
  }

  checkIfSearchInputHasValue(e) {
    if (!this.searchInput) return
    const searchValue = this.searchInput.value
    if (searchValue.trim().length === 0 || searchValue === '') {
      e.preventDefault()
      this.searchInput.setCustomValidity(
        t('general.search.empty_search_message')
      )
      this.form.reportValidity()
    }
  }

  onSearchInput(e) {
    const { value } = e.target

    if (value.length === 0) {
      if (this.searchResults) this.searchResults.innerHTML = ''
      this.refreshFocusTrap()
    }

    if (!this.searchInput) return
    this.searchInput.setCustomValidity('')
    this.searchTerm = value
    this.toggleSearchClear(value)
  }

  onSearchKeyDown(e) {
    // prevent esc from clearing the search input
    if (e.key === 'Escape') {
      e.preventDefault()
    }
  }

  searchChange(query) {
    const newSearchTerm = this.getQuery(query)
    if (newSearchTerm.length < 3) {
      this.searchActive = false
      return
    }

    this.searchActive = true
    this.getSearchResults(newSearchTerm)
  }

  async getSearchResults(query) {
    const queryKey = query.replace(' ', '-').toLowerCase()

    if (this.cachedResults?.[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey])
      return
    }

    const searchFields = [
      'body',
      'product_type',
      'tag',
      'title',
      'variants.barcode',
      'variants.sku',
      'variants.title',
      'vendor',
    ]

    const searchEndpoint = window.routes.predictive_search_url
    const queryParam = `q=${encodeURIComponent(query)}`
    const resourcesParam = `resources[type]=product`
    const fieldsParam = `resources[options][fields]=${searchFields.join(',')}`
    const sectionIdParam = `section_id=predictive-search`
    const searchUrl = `${searchEndpoint}?${queryParam}&${resourcesParam}&${fieldsParam}&${sectionIdParam}`

    try {
      const res = await fetch(searchUrl)
      if (!res.ok) throw new Error(res.status)

      const text = await res.text()
      const resultsMarkup = new DOMParser()
        .parseFromString(text, 'text/html')
        .querySelector('#shopify-section-predictive-search')

      this.cachedResults[queryKey] = resultsMarkup

      this.searchResultsUrl = `/search?${queryParam}&type=product`
      this.renderSearchResults(resultsMarkup)
    } catch (error) {
      console.error(error)
    }
  }

  renderSearchResults(resultsMarkup) {
    this.noResults = resultsMarkup.querySelector('[data-no-results]')
    if (this.noResults) { this.searchResultsUrl = '' }

    this.searchResults.innerHTML = resultsMarkup.innerHTML

    this.refreshFocusTrap()
    setTimeout(() => {
      this.checkOverflow()
    }, 0)
  }

  getQuery(value) {
    return value.trim()
  }

  onSearchClear() {
    this.searchInput.value = ''
    this.searchInput.dispatchEvent(new Event('input'))
    this.searchResultsUrl = ''

    this.searchInput.focus()
  }

  toggleSearchClear(value) {
    this.searchClear.toggleAttribute(this.classes.hidden, value.length === 0)

    if (value.length > 0) {
      const tempTextSpan = document.createElement('span')
      tempTextSpan.textContent = value
      tempTextSpan.classList.add('header-search__input')
      tempTextSpan.style.cssText =
        'white-space: nowrap; width: auto; position: absolute; top: -9999px; left: -9999px; display: block;'
      this.searchInput.insertAdjacentElement('afterend', tempTextSpan)
      const textWidth = tempTextSpan.offsetWidth
      tempTextSpan.remove()

      this.searchInput.style.width = `${textWidth + 30}px`
    } else {
      this.searchInput.style.width = ''
    }
  }

  refreshFocusTrap(timeout = 0) {
    const focusTrap = this.$focusTrap?.value
    setTimeout(() => {
      focusTrap?.refreshTrapFocus()
    }, timeout)
  }

  checkOverflow() {
    const searchResultsContainer = this.$searchResultsContainer?.value
    if (!searchResultsContainer) return

    const { scrollHeight, clientHeight } = searchResultsContainer
    const overflow = scrollHeight > clientHeight

    this.hasOverflow = overflow
  }

  render() {
    return html`
      <div
        class=${classMap({
          overlay: true,
          open: this.open,
        })}
        @click=${this.onCloseClick}
        aria-hidden="${!this.open}"
      ></div>
      <div
        class=${classMap({
          modal: true,
          open: this.open,
        })}
        role="dialog"
        aria-modal="true"
        aria-labelledby=${ifDefined(this.labelledBy)}
        aria-describedby=${ifDefined(this.describedBy)}
        @click=${this.closeFromModal}
        ?inert=${!this.open}
      >
        <hh-focus-trap active="${ifDefined(this.open)}" ${ref(this.$focusTrap)}>
          <slot name="search-form"></slot>
          <div
            id="predictive-search-results"
            role="listbox"
            class=${classMap({
              'search-results-container': true,
              active: this.searchActive,
              'has-overflow': this.searchActive && this.hasOverflow,
            })}
            ${ref(this.$searchResultsContainer)}
          >
            <div class="search-results-inner"  aria-live="polite" role="region">
              <slot name="search-results"></slot>
            ${(this.searchResultsUrl ?
              html`<hh-button
                theme='primary'
                href='${this.searchResultsUrl}'>
                ${window.searchStrings.viewAllLabel}
              </hh-button>`
              : null
            )}
            </div>
          </div>
        </hh-focus-trap>
      </div>
    `
  }

  static styles = css`
    :host *,
    :host *::before,
    :host *::after {
      box-sizing: border-box;
    }

    .overlay {
      position: fixed;
      inset: 0;
      z-index: 98;
      background-color: var(--hh-overlay-color, rgba(0, 0, 0, 0.5));
      backdrop-filter: blur(var(--hh-overlay-blur, 2.5px));
      -webkit-backdrop-filter: blur(var(--hh-overlay-blur, 2.5px));
      opacity: 0;
      pointer-events: none;
      transition: opacity var(--hh-overlay-duration, --hh-duration-long)
        ease-in-out;
      min-width: 100%;
      min-height: 100%;
      width: 100vw;
      height: 100vh;
      touch-action: none;
    }

    .overlay.open {
      opacity: 1;
      pointer-events: auto;
    }

    .modal {
      position: fixed;
      top: var(--hh-total-header-height, 0);
      right: 0;
      left: 0;
      bottom: auto;
      z-index: 99;
      width: 100vw;
      left: 50%;
      right: 50%;
      margin-left: -50vw;
      margin-right: -50vw;
      transform: translate3d(0, -300%, 0);
      transition: transform var(--hh-duration-long) ease;
    }

    .modal.open {
      transform: translate3d(0, 0, 0);
      overscroll-behavior: none;
    }

    ::slotted([slot='search-form']) {
      width: 100%;
    }

    .search-results-container {
      display: none;
      width: 100%;
      padding: var(--hh-spacing-xl) var(--hh-spacing-xs) var(--hh-spacing-2xl);
      background-color: var(--hh-color-light);
      overflow-y: auto;
      overscroll-behavior-y: none;
      height: calc(
        100svh - var(--hh-total-header-height) - var(--hh-search-form-height)
      );
    }

    .search-results-container:not(.has-overflow) {
      touch-action: none;
    }

    @media (min-width: 640px) {
      .search-results-container {
        padding: var(--hh-spacing-xl) var(--hh-spacing-xs);
      }
    }

    .search-results-container.active {
      display: block;
    }

    :host([no-results]) .search-results-container.active {
      overflow-y: hidden;
      touch-action: none;
    }

    .search-results-inner {
      margin: 0 auto;
      max-width: 120.8rem;
      display: flex;
      flex-direction: column;
    }

    :host([active]:not([no-results])) ::slotted([slot='search-results']) {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      column-gap: var(--hh-spacing-md);
      row-gap: var(--hh-spacing-xl);
    }

    hh-button[theme='primary'] {
      --hh-background: var(--hh-color-brand-primary);
      --hh-background-hover: transparent;
      --hh-color-disabled: var(--hh-color-light);
      --hh-background-disabled: var(--hh-color-subdued);
      --hh-font: var(--hh-button-large-font);
      --hh-letter-spacing: var(--hh-button-letter-spacing);
      --hh-padding: var(--hh-spacing-xs) var(--hh-spacing-2xl);
      --hh-height: auto;
      --hh-max-height: auto;
      --hh-max-width: 22rem;
      --hh-text-transform: uppercase;
      --hh-transition: all var(--hh-duration-default) ease;
      --hh-border-bottom-offset: -1px;
      --hh-border-radius: 9999px;
      margin: var(--hh-spacing-xl) auto;
    }

    @media (min-width: 640px) {
      :host([active]:not([no-results])) ::slotted([slot='search-results']) {
        grid-template-columns: repeat(3, 1fr);
      }
    }

    @media (min-width: 1024px) {
      :host([active]:not([no-results])) ::slotted([slot='search-results']) {
        grid-template-columns: repeat(4, 1fr);
      }
    }
  `
}

if (!customElements.get('hh-search-modal')) {
  customElements.define('hh-search-modal', HHSearchModal);
}
